import React, { useEffect } from 'react'
import { useStore } from '@/store'
import { HexHeader } from '@/views/hexView/HexHeader'
import { HexGrid } from '@/views/hexView/HexGrid'
import { useHexItems, useHexInactiveCatalogPending } from '@/hooks/useHexItems'
import { useHexExpansion } from '@/hooks/useHexExpansion'
import { useStableHexOrder } from '@/hooks/useStableHexOrder'
import { countsForWorldHops, useHexCounts } from '@/hooks/useViewCounts'

export default function HexView() {
  const dimension = useStore((s) => s.dimension)
  const drillRegion = useStore((s) => s.drillRegion)
  const drillStream = useStore((s) => s.drillStream)
  const drillToStream = useStore((s) => s.drillToStream)
  const descendToStreams = useStore((s) => s.descendToStreams)
  const dataDebouncing = useStore((s) => s.dataDebouncing)
  const streamsLoading = useStore((s) => s.streamsLoading)
  const wsUiPaused = useStore((s) => s.wsUiPaused)
  const includeInactive = useStore((s) => s.includeInactive)

  const liveItems = useHexItems()
  const catalogPending = useHexInactiveCatalogPending()
  const { expandedId, phase, toggle, close } = useHexExpansion()

  // While a tile is expanded (or mid open/close animation), pin the grid order so
  // the focused tile can't be re-sorted out from under the user. Data stays live;
  // only positions freeze. Order resumes when fully collapsed.
  const frozen = expandedId !== null || phase === 'closing' || phase === 'reflow'
  const hexItems = useStableHexOrder(liveItems, frozen)

  const streamsGateLoading = dataDebouncing || streamsLoading || wsUiPaused || catalogPending

  // Unified counting: totals come from server-aggregated node totals (same
  // semantics as the World view's hop sums), never rows.length.
  const counts = useHexCounts(hexItems)

  // Dev tripwire: at GLOBAL scope (no drill, no geo filters) the Hex total must
  // equal the World map's — a mismatch means the aggregation paths diverged again.
  const worldFlow = useStore((s) => s.worldFlowResult)
  const drillNode = useStore((s) => s.drillNode)
  const filters = useStore((s) => s.filters)
  useEffect(() => {
    if (!import.meta.env.DEV) return
    if (streamsGateLoading || !worldFlow || drillNode || dimension !== 'stream') return
    const geoScoped =
      filters.continent.length > 0 || filters.country.length > 0 ||
      filters.region.length > 0 || filters.city.length > 0
    if (geoScoped) return
    const world = countsForWorldHops(worldFlow.hops)
    if (world.totalStreams > 0 && counts.totalStreams > 0 && world.totalStreams !== counts.totalStreams) {
      console.warn('[count-parity] World vs Hex stream totals disagree for the same scope', {
        world: world.totalStreams,
        hex: counts.totalStreams,
      })
    }
  }, [streamsGateLoading, worldFlow, drillNode, dimension, filters, counts.totalStreams])

  const handleDrill = (id: string) => {
    if (dimension === 'stream') drillToStream(id)
    else if (dimension === 'studio' || dimension === 'game') descendToStreams(dimension, id)
  }

  return (
    <div className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <HexHeader
        dimension={dimension}
        drillRegion={drillRegion}
        itemCount={streamsGateLoading ? 0 : counts.itemCount}
        streamTotal={streamsGateLoading ? 0 : counts.totalStreams}
        orderPaused={frozen}
      />

      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, overflowX: 'hidden', overflowY: 'auto', scrollbarGutter: 'stable' }}>
            {streamsGateLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--t-2)', fontSize: 12 }}>
                {catalogPending ? 'Loading catalog streams…' : 'Loading streams…'}
              </div>
            ) : hexItems.length > 0 ? (
              <HexGrid
                items={hexItems}
                selectedId={drillStream}
                expandedId={expandedId}
                phase={phase}
                canDrill={dimension === 'stream' || dimension === 'studio' || dimension === 'game'}
                onToggle={toggle}
                onDrill={handleDrill}
                onClose={close}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--t-2)', fontSize: 12 }}>
                {includeInactive && dimension === 'stream'
                  ? 'No catalog streams match the current filters.'
                  : 'No streams for the current filters.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
